import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import axios from "axios";
import {
  Percent,
  Plus,
  Trash2,
  Loader2,
  ChevronDown,
  ChevronUp,
  Building2,
  Globe,
  Copy,
  RotateCcw,
  FileText,
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { Label } from "./ui/label";
import { Input } from "./ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import { useAuth } from "../context/AuthContext";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const REGIMI_IVA = [
  { value: "esente_art10", label: "Esente Art.10" },
  { value: "iva_4", label: "IVA 4%" },
  { value: "iva_22", label: "IVA 22%" },
  { value: "esclusa_art15", label: "Escl. Art.15" },
];

function newVoce() {
  return {
    id: crypto.randomUUID(),
    nome: "",
    percentuale: "",
    regime_iva: "esente_art10",
  };
}

function vociFromApi(list) {
  if (!Array.isArray(list) || list.length === 0) return [];
  return list.map((v) => ({
    id: v.id || crypto.randomUUID(),
    nome: v.nome || "",
    percentuale: v.percentuale ?? "",
    regime_iva: v.regime_iva || "esente_art10",
  }));
}

function sumPercentuali(voci) {
  return voci.reduce((acc, v) => {
    const n = parseFloat(String(v.percentuale).replace(",", "."));
    return acc + (Number.isFinite(n) ? n : 0);
  }, 0);
}

/**
 * Impostazioni per suddividere la retta mensile in voci percentuali in fattura.
 */
export default function ImpostazioniRettaMensile({ token, orgStrutture = [] }) {
  const { activeStruttura } = useAuth();
  const [showSection, setShowSection] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [configScope, setConfigScope] = useState(null);
  const [isGlobal, setIsGlobal] = useState(true);
  const [copying, setCopying] = useState(false);
  const [voci, setVoci] = useState([]);
  const configRef = useRef({});
  const fetchGenRef = useRef(0);
  const multiStruttura = orgStrutture.length > 1;
  const orgStruttureKey = orgStrutture.map((s) => s.id || s.zohoRecordId || "").join(",");

  const fetchConfig = useCallback(
    async (strutturaId = null) => {
      const gen = ++fetchGenRef.current;
      setLoading(true);
      try {
        const params = strutturaId ? { strutturaId } : {};
        const res = await axios.get(`${API_URL}/api/fatturazione/config`, {
          headers: { Authorization: `Bearer ${token}` },
          params,
        });
        if (gen !== fetchGenRef.current) return;
        const cfg = res.data.config || {};
        configRef.current = cfg;
        setIsGlobal(res.data._isGlobal !== false);
        setVoci(vociFromApi(cfg.voci_retta_mensile));
      } catch {
        /* silent */
      } finally {
        if (gen === fetchGenRef.current) setLoading(false);
      }
    },
    [token]
  );

  useEffect(() => {
    if (!token) return;
    let scope = null;
    if (multiStruttura && activeStruttura) {
      const sid = activeStruttura.id || activeStruttura.zohoRecordId;
      const match = orgStrutture.find(
        (s) => s.id === sid || s.zohoRecordId === sid || s.id === activeStruttura.id
      );
      scope = match?.id || sid || null;
    }
    setConfigScope(scope);
    fetchConfig(scope);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- orgStruttureKey
  }, [
    token,
    multiStruttura,
    activeStruttura?.id,
    activeStruttura?.zohoRecordId,
    orgStruttureKey,
    fetchConfig,
  ]);

  const handleScopeChange = (value) => {
    const scope = value === "__global__" ? null : value;
    setConfigScope(scope);
    fetchConfig(scope);
  };

  const handleSave = async () => {
    const payloadVoci = [];
    for (const v of voci) {
      const nome = (v.nome || "").trim();
      const pct = parseFloat(String(v.percentuale).replace(",", "."));
      if (!nome) {
        toast.error("Ogni voce deve avere un nome");
        return;
      }
      if (!Number.isFinite(pct) || pct <= 0 || pct > 100) {
        toast.error(`Percentuale non valida per "${nome}"`);
        return;
      }
      payloadVoci.push({
        id: v.id,
        nome,
        percentuale: pct,
        regime_iva: v.regime_iva || "esente_art10",
      });
    }

    const totPct = payloadVoci.reduce((a, v) => a + v.percentuale, 0);
    if (totPct > 100) {
      toast.error("La somma delle percentuali non può superare il 100%");
      return;
    }

    setSaving(true);
    try {
      await axios.put(
        `${API_URL}/api/fatturazione/config`,
        {
          ...configRef.current,
          voci_retta_mensile: payloadVoci,
          strutturaId: configScope || null,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Impostazioni retta mensile salvate");
      await fetchConfig(configScope);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore nel salvataggio");
    } finally {
      setSaving(false);
    }
  };

  const handleCopyToStruttura = async () => {
    if (!configScope) return;
    setCopying(true);
    try {
      await axios.post(
        `${API_URL}/api/fatturazione/config/copy-to-struttura`,
        { strutturaId: configScope },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Configurazione copiata alla struttura");
      await fetchConfig(configScope);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore nella copia");
    } finally {
      setCopying(false);
    }
  };

  const handleResetToGlobal = async () => {
    if (!configScope) return;
    setCopying(true);
    try {
      await axios.delete(`${API_URL}/api/fatturazione/config/struttura/${configScope}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success("Ripristinata configurazione globale");
      await fetchConfig(configScope);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore nel ripristino");
    } finally {
      setCopying(false);
    }
  };

  const totalePct = sumPercentuali(voci);

  return (
    <Card className="card-base">
      <CardHeader className="pb-2 cursor-pointer" onClick={() => setShowSection(!showSection)}>
        <div className="flex items-center justify-between">
          <CardTitle className="text-sm md:text-base flex items-center gap-2">
            <Percent className="w-4 h-4 md:w-5 md:h-5 text-blue-600" />
            Impostazioni Retta Mensile
            {voci.length > 0 && (
              <Badge variant="outline" className="text-[10px]">
                {voci.length} {voci.length === 1 ? "voce" : "voci"}
              </Badge>
            )}
          </CardTitle>
          {showSection ? (
            <ChevronUp className="w-4 h-4 text-slate-400" />
          ) : (
            <ChevronDown className="w-4 h-4 text-slate-400" />
          )}
        </div>
        <p className="text-xs text-slate-500 mt-1">
          Suddividi la retta mensile in voci percentuali con aliquota IVA dedicata
        </p>
      </CardHeader>

      {showSection && (
        <CardContent className="space-y-6">
          {loading ? (
            <div className="text-center py-6">
              <Loader2 className="w-5 h-5 animate-spin mx-auto text-slate-400" />
            </div>
          ) : (
            <>
              {orgStrutture.length > 1 && (
                <div className="space-y-3 p-4 bg-blue-50/50 rounded-lg border border-blue-100">
                  <div className="flex flex-col sm:flex-row sm:items-center gap-3">
                    <div className="flex-1">
                      <Label className="font-medium text-sm flex items-center gap-1.5">
                        <Building2 className="w-4 h-4 text-blue-600" />
                        Ambito configurazione
                      </Label>
                    </div>
                    <Select value={configScope || "__global__"} onValueChange={handleScopeChange}>
                      <SelectTrigger className="w-full sm:w-64 bg-white">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="__global__">
                          <span className="flex items-center gap-2">
                            <Globe className="w-3.5 h-3.5" /> Configurazione Globale
                          </span>
                        </SelectItem>
                        {orgStrutture.map((s) => (
                          <SelectItem key={s.id} value={s.id || s.zohoRecordId}>
                            <span className="flex items-center gap-2">
                              <Building2 className="w-3.5 h-3.5" /> {s.name}
                            </span>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {configScope && (
                    <div className="flex items-center gap-2 flex-wrap">
                      {isGlobal ? (
                        <>
                          <Badge variant="outline" className="text-xs bg-amber-50 text-amber-700 border-amber-200">
                            <Globe className="w-3 h-3 mr-1" /> Usa configurazione globale
                          </Badge>
                          <Button
                            variant="outline"
                            size="sm"
                            className="text-xs h-7"
                            onClick={handleCopyToStruttura}
                            disabled={copying}
                          >
                            {copying ? (
                              <Loader2 className="w-3 h-3 animate-spin mr-1" />
                            ) : (
                              <Copy className="w-3 h-3 mr-1" />
                            )}
                            Personalizza per questa struttura
                          </Button>
                        </>
                      ) : (
                        <>
                          <Badge className="text-xs bg-emerald-50 text-emerald-700 border-emerald-200">
                            <Building2 className="w-3 h-3 mr-1" /> Configurazione personalizzata
                          </Badge>
                          <Button
                            variant="outline"
                            size="sm"
                            className="text-xs h-7 text-red-600 hover:text-red-700 hover:bg-red-50"
                            onClick={handleResetToGlobal}
                            disabled={copying}
                          >
                            {copying ? (
                              <Loader2 className="w-3 h-3 animate-spin mr-1" />
                            ) : (
                              <RotateCcw className="w-3 h-3 mr-1" />
                            )}
                            Ripristina globale
                          </Button>
                        </>
                      )}
                    </div>
                  )}
                </div>
              )}

              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <Label className="font-medium text-sm text-slate-700">Voci retta mensile</Label>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="text-xs h-8"
                    onClick={() => setVoci((prev) => [...prev, newVoce()])}
                  >
                    <Plus className="w-3.5 h-3.5 mr-1" />
                    Aggiungi voce
                  </Button>
                </div>

                {voci.length === 0 ? (
                  <div className="text-center py-8 text-sm text-slate-500 bg-slate-50 rounded-lg border border-dashed">
                    <FileText className="w-8 h-8 mx-auto mb-2 text-slate-300" />
                    Nessuna voce configurata. La fattura conterrà l&apos;intera retta mensile.
                  </div>
                ) : (
                  <div className="space-y-2">
                    {voci.map((voce, idx) => (
                      <div
                        key={voce.id}
                        className="grid grid-cols-1 md:grid-cols-12 gap-2 p-3 bg-slate-50 rounded-lg border items-end"
                      >
                        <div className="md:col-span-5">
                          <Label className="text-xs text-slate-500">Nome voce</Label>
                          <Input
                            value={voce.nome}
                            onChange={(e) =>
                              setVoci((prev) =>
                                prev.map((v, i) => (i === idx ? { ...v, nome: e.target.value } : v))
                              )
                            }
                            className="mt-1"
                            placeholder="Es. Contributo ASL"
                          />
                        </div>
                        <div className="md:col-span-3">
                          <Label className="text-xs text-slate-500">% della retta</Label>
                          <Input
                            type="number"
                            min="0"
                            max="100"
                            step="0.01"
                            value={voce.percentuale}
                            onChange={(e) =>
                              setVoci((prev) =>
                                prev.map((v, i) =>
                                  i === idx ? { ...v, percentuale: e.target.value } : v
                                )
                              )
                            }
                            className="mt-1 font-mono"
                            placeholder="10"
                          />
                        </div>
                        <div className="md:col-span-3">
                          <Label className="text-xs text-slate-500">Aliquota IVA</Label>
                          <Select
                            value={voce.regime_iva}
                            onValueChange={(val) =>
                              setVoci((prev) =>
                                prev.map((v, i) => (i === idx ? { ...v, regime_iva: val } : v))
                              )
                            }
                          >
                            <SelectTrigger className="mt-1">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {REGIMI_IVA.map((r) => (
                                <SelectItem key={r.value} value={r.value}>
                                  {r.label}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="md:col-span-1 flex justify-end">
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            className="h-9 w-9 text-red-500 hover:text-red-600 hover:bg-red-50"
                            onClick={() => setVoci((prev) => prev.filter((_, i) => i !== idx))}
                            aria-label="Rimuovi voce"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    ))}

                    <p className="text-xs text-slate-500">
                      Totale percentuali:{" "}
                      <span className={totalePct > 100 ? "text-red-600 font-medium" : "font-medium"}>
                        {totalePct.toFixed(2)}%
                      </span>
                      {totalePct <= 100 && totalePct > 0 && (
                        <span> — retta base in fattura: {(100 - totalePct).toFixed(2)}%</span>
                      )}
                    </p>
                  </div>
                )}

                <div className="bg-blue-50 rounded-lg p-3 text-xs text-slate-600 border border-blue-100">
                  <p className="font-medium text-slate-700 mb-1">Esempio</p>
                  <p>
                    Con retta 1.000 €, voce1 al 10% e voce2 al 20%, la fattura conterrà: retta base 700 €,
                    voce1 100 €, voce2 200 € — totale 1.000 €.
                  </p>
                </div>
              </div>

              <div className="flex justify-end pt-2 border-t">
                <Button onClick={handleSave} disabled={saving}>
                  {saving ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin mr-2" />
                      Salvataggio...
                    </>
                  ) : (
                    "Salva impostazioni"
                  )}
                </Button>
              </div>
            </>
          )}
        </CardContent>
      )}
    </Card>
  );
}
